import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase/supabaseClient';
import { Session, User } from '@supabase/supabase-js';
import { isUserAdmin } from '../lib/auth/llnRoleService';
import { adminConsole } from '../lib/utils/adminConsole';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import IdleTimeoutWarningModal from '../components/common/IdleTimeoutWarningModal';
import {
  updateAllRolesLastLogin,
  incrementFailedLoginAttempts,
  shouldLockAccount
} from '../lib/auth/loginTrackingService';
import { securityMonitoring } from '../lib/security/securityLogging';
import { isImpersonating } from '../lib/auth/impersonationService';

console.log('[AuthContext] Module loaded - idle timeout feature active');

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, recaptchaToken?: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error: any; data: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => Promise<boolean>;
  getUserRole: () => Promise<string>;
  userRole: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('user'); // Default to 'user', will be updated to 'admin' if applicable
  const [roleLoading, setRoleLoading] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [sessionRestored, setSessionRestored] = useState(false);
  
  // Role caching to prevent redundant database calls
  const roleCache = useRef<{ userId: string; isAdmin: boolean; timestamp: number } | null>(null);
  const ROLE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  const roleCheckInProgress = useRef<Promise<boolean> | null>(null);
  const sessionRestorationComplete = useRef(false);

  useEffect(() => {
    console.log('[AuthContext] Setting up auth state listener');
    
    // Listen for auth changes FIRST before getting session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[AuthContext] Auth state changed:', event, 'has user:', !!newSession?.user);
        
        // Handle signed out event
        if (event === 'SIGNED_OUT') {
          roleCache.current = null;
          roleCheckInProgress.current = null;
          setUserRole('user'); // Reset to default 'user' role
          setSessionRestored(false);
          sessionRestorationComplete.current = false;
        }
        
        // Handle signed in event - update last_login only if NOT impersonating
        if (event === 'SIGNED_IN' && newSession?.user) {
          console.log('[AuthContext] Processing SIGNED_IN event for user:', newSession.user.email);
          
          // Don't block the UI - update login tracking in background
          (async () => {
            try {
              const isImpersonationSession = isImpersonating();
              
              if (!isImpersonationSession) {
                // Only update last_login for real user logins, not impersonation
                console.log('[AuthContext] Updating last login (non-blocking)...');
                await updateAllRolesLastLogin(newSession.user.id);
                console.log('[AuthContext] Last login updated');
                
                await securityMonitoring.logEvent(
                  securityMonitoring.eventTypes.LOGIN_SUCCESS,
                  { userId: newSession.user.id, email: newSession.user.email },
                  'info'
                );
              } else {
                // Log that this is an impersonation session (already logged in edge function)
                console.log('[AuthContext] Impersonation session detected, skipping last_login update');
              }
            } catch (error) {
              console.error('[AuthContext] Error updating last login (non-blocking):', error);
              // Don't block the sign-in process if logging fails
            }
          })();
          
          // For new sign-ins (not page refresh), enable idle timeout immediately
          if (!sessionRestorationComplete.current) {
            setSessionRestored(true);
            sessionRestorationComplete.current = true;
          }
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // If there's no user, we can set loading to false immediately
        // Otherwise, wait for role to load
        if (!newSession?.user) {
          setLoading(false);
        }
      }
    );

    // Get initial session AFTER setting up the listener
    const getInitialSession = async () => {
      console.log('[AuthContext] Starting session restoration...');
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthContext] Session restoration error:', error);
          console.log('[AuthContext] Error details:', {
            message: error.message,
            status: error.status,
            name: error.name
          });
          
          // Clear the potentially corrupted session
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setLoading(false);
          sessionRestorationComplete.current = true;
          setSessionRestored(true);
          console.log('[AuthContext] Session cleared due to error');
          return;
        }
        
        if (initialSession?.user) {
          console.log('[AuthContext] Session restored successfully for user:', initialSession.user.email);
          setSession(initialSession);
          setUser(initialSession.user);
          sessionRestorationComplete.current = true;
          // Don't set loading to false yet - wait for role to load
          console.log('[AuthContext] User found, waiting for role to load...');
        } else {
          console.log('[AuthContext] No active session found');
          setSession(null);
          setUser(null);
          setLoading(false);
          setSessionRestored(true);
          sessionRestorationComplete.current = true;
        }
      } catch (error) {
        console.error('[AuthContext] Exception during session restoration:', error);
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setLoading(false);
        sessionRestorationComplete.current = true;
        setSessionRestored(true);
      }
    };

    getInitialSession();

    return () => {
      console.log('[AuthContext] Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  // Load user role when user changes
  useEffect(() => {
    const loadUserRole = async () => {
      if (user) {
        console.log('[AuthContext] Loading role for user:', user.email);
        setRoleLoading(true);
        // Keep loading true until role is loaded to prevent premature access checks
        
        try {
          const isAdmin = await getCachedAdminStatus(user.id);
          const role = isAdmin ? 'admin' : 'user';
          setUserRole(role);
          adminConsole.setAdminStatus(isAdmin);
          console.log('[AuthContext] Role loaded:', role);
        } catch (error) {
          console.error('[AuthContext] Error loading user role:', error);
          setUserRole('user');
          adminConsole.setAdminStatus(false);
        } finally {
          // Set loading to false AFTER role is loaded
          setLoading(false);
          setRoleLoading(false);
          // Enable idle timeout after role is loaded and session is restored
          // This prevents race condition on page refresh
          if (sessionRestorationComplete.current) {
            setSessionRestored(true);
            console.log('[AuthContext] ✅ Session fully restored, idle timeout enabled');
          }
        }
      } else {
        console.log('[AuthContext] No user, resetting role');
        setUserRole('user'); // Reset to default 'user' role when no user
        adminConsole.setAdminStatus(false);
        roleCache.current = null;
      }
    };

    loadUserRole();
  }, [user?.id]);

  // Initialize admin console on app start
  useEffect(() => {
    adminConsole.init();
  }, []);

  const signIn = async (email: string, password: string, recaptchaToken?: string) => {
    try {
      // Verify reCAPTCHA token server-side if provided
      if (recaptchaToken) {
        console.log('[AuthContext] Verifying reCAPTCHA token server-side');
        
        try {
          const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-recaptcha', {
            body: {
              token: recaptchaToken,
              action: 'login'
            }
          });

          if (verifyError || !verifyData?.success) {
            console.error('[AuthContext] reCAPTCHA verification failed:', verifyError || verifyData);
            await securityMonitoring.logEvent(
              'recaptcha_verification_failed',
              { email, error: verifyError?.message || 'Verification failed' },
              'warning'
            );
            return {
              error: {
                message: 'Security verification failed. Please try again or contact support if the problem persists.'
              }
            };
          }

          console.log('[AuthContext] reCAPTCHA verified successfully, score:', verifyData.score);
          await securityMonitoring.logEvent(
            'recaptcha_verification',
            { email, hasToken: true, score: verifyData.score },
            'info'
          );
        } catch (recaptchaError) {
          console.error('[AuthContext] reCAPTCHA verification error:', recaptchaError);
          await securityMonitoring.logEvent(
            'recaptcha_verification_error',
            { email, error: String(recaptchaError) },
            'error'
          );
          return {
            error: {
              message: 'Security verification encountered an error. Please try again.'
            }
          };
        }
      }

      // Attempt to sign in after reCAPTCHA verification (if required)
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        // Log failed login attempt
        await securityMonitoring.logEvent(
          securityMonitoring.eventTypes.LOGIN_FAILURE,
          { email, error: error.message },
          'warning'
        );
        
        // Increment failed login attempts
        await incrementFailedLoginAttempts(email);
        
        // Check if this failure should lock the account
        const isLocked = await shouldLockAccount(email);
        if (isLocked) {
          await securityMonitoring.logEvent(
            'account_locked',
            { email, reason: 'too_many_failed_attempts' },
            'error'
          );
          return {
            error: {
              message: 'Account locked due to too many failed login attempts. Please contact an administrator to unlock your account.'
            }
          };
        }
        
        return { error };
      }

      // Before allowing successful login, check if account was already locked
      const wasLocked = await shouldLockAccount(email);
      if (wasLocked) {
        // Sign out immediately
        await supabase.auth.signOut();
        const lockError = {
          message: 'Your account is locked due to too many failed login attempts. Please contact an administrator to unlock your account.'
        };
        await securityMonitoring.logEvent(
          'login_attempt_blocked',
          { email, reason: 'account_locked' },
          'warning'
        );
        return { error: lockError };
      }

      // Successful login - update last_login and reset failed attempts
      // Note: This is for password-based logins only
      // Magic link logins (including impersonation) are handled in onAuthStateChange
      if (data.user) {
        await updateAllRolesLastLogin(data.user.id);
        await securityMonitoring.logEvent(
          securityMonitoring.eventTypes.LOGIN_SUCCESS,
          { userId: data.user.id, email },
          'info'
        );
      }

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Exception in signIn:', error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      // Public sign-up is disabled - only admin invitations are allowed
      return {
        data: null,
        error: { message: 'Public registration is disabled. Please contact an administrator for an invitation.' }
      };
    } catch (error) {
      console.error('Error signing up:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      console.log('[AuthContext] Signing out user');
      
      // Clear role cache and state immediately
      roleCache.current = null;
      roleCheckInProgress.current = null;
      setUserRole('user'); // Reset to default 'user' role
      
      // Sign out first to ensure user is logged out regardless of logging issues
      await supabase.auth.signOut();
      
      // Log logout event after signing out (non-blocking)
      // If this fails, it won't prevent the sign-out from completing
      if (user) {
        try {
          await securityMonitoring.logEvent(
            securityMonitoring.eventTypes.LOGOUT,
            { userId: user.id, email: user.email },
            'info'
          );
        } catch (logError) {
          console.warn('[AuthContext] Failed to log logout event:', logError);
          // Don't throw - sign-out should still succeed
        }
      }
    } catch (error) {
      console.error('[AuthContext] Error signing out:', error);
      // Even if there's an error, try to clear local state
      roleCache.current = null;
      roleCheckInProgress.current = null;
      setUserRole('user'); // Reset to default 'user' role
      setSession(null);
      setUser(null);
    }
  };

  // Idle timeout management - use useCallback to prevent recreating functions
  const handleIdleWarning = useCallback(() => {
    console.log('[AuthContext] Idle warning triggered');
    setShowIdleWarning(true);
  }, []);

  const handleIdleTimeout = useCallback(async () => {
    console.log('[AuthContext] Idle timeout triggered, signing out');
    setShowIdleWarning(false);
    await signOut();
  }, []);

  const handleStayLoggedIn = useCallback(() => {
    console.log('[AuthContext] User chose to stay logged in');
    setShowIdleWarning(false);
  }, []);

  // Initialize idle timeout hook - only once with stable callbacks
  // Only enable after session restoration is complete to prevent premature timeout on page refresh
  const idleTimeoutEnabled = !!user && sessionRestored;
  console.log('[AuthContext] Idle timeout status:', {
    hasUser: !!user,
    sessionRestored,
    enabled: idleTimeoutEnabled
  });
  
  const { remainingTime, resetTimer: resetIdleTimer } = useIdleTimeout({
    onWarning: handleIdleWarning,
    onTimeout: handleIdleTimeout,
    enabled: idleTimeoutEnabled // Only enable when user is logged in AND session is fully restored
  });

  // Handle stay logged in with resetTimer
  const handleStayLoggedInWithReset = useCallback(() => {
    handleStayLoggedIn();
    if (resetIdleTimer) {
      resetIdleTimer();
    }
  }, [handleStayLoggedIn, resetIdleTimer]);

  const getCachedAdminStatus = async (userId: string): Promise<boolean> => {
    // If a role check is already in progress, wait for it
    if (roleCheckInProgress.current) {
      return roleCheckInProgress.current;
    }

    const now = Date.now();
    
    // Check if cache is valid
    if (
      roleCache.current &&
      roleCache.current.userId === userId &&
      now - roleCache.current.timestamp < ROLE_CACHE_DURATION
    ) {
      return roleCache.current.isAdmin;
    }
    
    // Start a new role check and store the promise
    roleCheckInProgress.current = (async () => {
      try {
        const isAdmin = await isUserAdmin(userId);
        
        // Update cache
        roleCache.current = {
          userId,
          isAdmin,
          timestamp: now
        };
        
        return isAdmin;
      } finally {
        // Clear the in-progress flag
        roleCheckInProgress.current = null;
      }
    })();
    
    return roleCheckInProgress.current;
  };

  const hasRole = async (role: string): Promise<boolean> => {
    if (!user) return false;
    
    if (role === 'admin') {
      // Return cached role if available, otherwise fetch
      if (userRole === 'admin') return true;
      if (userRole === 'user') return false;
      return await getCachedAdminStatus(user.id);
    }
    
    // Default role is 'user' for all authenticated users
    return role === 'user';
  };

  const getUserRole = async (): Promise<string> => {
    if (!user) return 'user';
    
    // Return cached role if available
    if (userRole) {
      return userRole;
    }
    
    const isAdmin = await getCachedAdminStatus(user.id);
    return isAdmin ? 'admin' : 'user';
  };

  const value = {
    user,
    session,
    // Only show loading for initial auth check, not role loading
    // This prevents the entire app from being blocked while role is fetched
    loading,
    signIn,
    signUp,
    signOut,
    hasRole,
    getUserRole,
    userRole, // Expose the cached role
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && (
        <IdleTimeoutWarningModal
          isOpen={showIdleWarning}
          remainingTime={remainingTime}
          onStayLoggedIn={handleStayLoggedInWithReset}
          onLogout={handleIdleTimeout}
        />
      )}
    </AuthContext.Provider>
  );
};
